import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { TenantId } from '@etiquetachef/types';
import { HardDeleteTenantDialog } from './HardDeleteTenantDialog';

type Props = {
  tenantId: TenantId;
  tenantName: string;
};

/**
 * Aba "Excluir" — montada pela `TenantDetailPage` quando o tenant está
 * vazio (`useTenantDeletable` === DELETABLE) OU tem dados
 * (=== BLOCKED), oferecendo a exclusão DEFINITIVA (hard-delete imediato).
 * O `HardDeleteTenantDialog` adapta a confirmação: tenant vazio confirma
 * pelo nome (sem `force`); tenant com dados exige o purge forçado
 * (checkbox + tenantId exato, `force: true` — issue #83). O guard
 * autoritativo é server-side em `hardDeleteEmptyTenant`.
 *
 * Origem: sem issue — commit a62adb1e3, 2026-06-11 22:55 +0200.
 *
 * Contrato: só abre o diálogo; não lê nem escreve nada. A página também a
 * monta com `useTenantDeletable` em `ERROR` (o diálogo então exige o purge
 * forçado).
 *
 * Usado por: `routes/TenantDetailPage.tsx` (aba `delete`).
 */
export function TenantDeleteTab({ tenantId, tenantName }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Container
      header={
        <Header variant="h2">{t('tenantDetail.actions.hardDeleteDialog.title')}</Header>
      }
    >
      <SpaceBetween size="m">
        <Alert type="warning">{t('tenantDetail.actions.hardDeleteDialog.warning')}</Alert>
        <Box>
          <Button
            iconName="delete-marker"
            onClick={(): void => {
              setOpen(true);
            }}
          >
            {t('tenantDetail.actions.hardDeleteDialog.button')}
          </Button>
        </Box>
      </SpaceBetween>
      <HardDeleteTenantDialog
        open={open}
        onClose={(): void => {
          setOpen(false);
        }}
        tenantId={tenantId}
        tenantName={tenantName}
      />
    </Container>
  );
}
